import { useSyncExternalStore } from 'react';

export type Route =
  | { name: 'dashboard' | 'markets' | 'faucet' | 'history' | 'status' }
  | { name: 'reserve'; symbol: string };

const STATIC: Record<string, Route['name']> = { '/': 'dashboard', '/markets': 'markets', '/faucet': 'faucet', '/history': 'history', '/status': 'status' };
const listeners = new Set<() => void>();

function parse(pathname: string): Route {
  const m = /^\/reserve\/([A-Za-z0-9.]{1,12})\/?$/.exec(pathname);
  if (m) return { name: 'reserve', symbol: m[1]!.toUpperCase() };
  const name = STATIC[pathname.replace(/\/$/, '') || '/'];
  return name && name !== 'reserve' ? { name } : { name: 'dashboard' };
}

// useSyncExternalStore needs a referentially stable snapshot: re-parse only when the path changes.
let cachedPath = '';
let cached: Route = { name: 'dashboard' };
const current = (): Route => {
  if (location.pathname !== cachedPath) { cachedPath = location.pathname; cached = parse(cachedPath); }
  return cached;
};

window.addEventListener('popstate', () => listeners.forEach((l) => l()));

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === location.pathname) return;
  history[opts.replace ? 'replaceState' : 'pushState'](null, '', to + location.search);
  listeners.forEach((l) => l());
  window.scrollTo({ top: 0 });
}

export const useRoute = (): Route =>
  useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, current);

/** Props for an in-app link: a real href (middle-click, copy link) with client-side navigation on plain clicks. */
export const linkTo = (to: string) => ({
  href: to,
  onClick: (e: { preventDefault(): void; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; button: number }) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to);
  },
});
